import { useState } from 'react';
import type { ApiClient, CapabilityRunView } from '../api/client';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { Spinner } from '../components/Spinner';
import { StatusBadge } from '../components/StatusBadge';
import { ConsentDialog } from './ConsentDialog';
import { useCapabilityProbe } from './useCapabilityProbe';
import { formatMicroUsd, VERDICT_SUMMARY, verdictReason } from './capabilityCopy';
import styles from './CapabilityCheck.module.css';

export interface CapabilityCheckProps {
  client: ApiClient;
  orgId: string;
  /** Org-admins can run a probe; everyone else sees the latest result read-only. */
  canRun: boolean;
  /**
   * When set, the check is blocked and this message is shown instead of the run
   * button. Used when the on-screen model differs from the saved active model —
   * the probe runs against the SAVED config, so running now would return a result
   * for a different model than the one the admin is editing (#432).
   */
  blockedReason?: string | null | undefined;
  /** Test seam for the poll loop. */
  pollIntervalMs?: number | undefined;
}

function liveMessage(phase: ReturnType<typeof useCapabilityProbe>['phase']): string {
  if (phase.kind === 'running') return 'Checking your model’s capability…';
  if (phase.kind === 'failed') return 'The capability check couldn’t be completed. You can try again.';
  if (phase.kind === 'result' && phase.run.status) {
    const score = phase.run.score != null ? ` Score ${Math.round(phase.run.score)}%.` : '';
    return `${VERDICT_SUMMARY[phase.run.status]}. ${verdictReason(phase.run.status, phase.run.reason)}${score}`;
  }
  return '';
}

function Verdict({ run }: { run: CapabilityRunView }) {
  if (!run.status) return null;
  return (
    <div className={styles.verdict} data-testid="capability-verdict">
      <StatusBadge kind="capability" status={run.status} />
      {run.score != null ? (
        <span className={styles.score}>Score {Math.round(run.score)}%</span>
      ) : null}
      <p className={styles.reason}>{verdictReason(run.status, run.reason)}</p>
    </div>
  );
}

/**
 * BYOK model capability check (Story 31-B2). Renders the async consent→probe→verdict
 * flow on the LLM-key settings screen. Admins run it; all members see the latest
 * verdict. ONE visually-hidden polite live region announces pending once and the
 * final verdict; warnings are visual-only to avoid duplicate announcements.
 */
export function CapabilityCheck({
  client,
  orgId,
  canRun,
  blockedReason,
  pollIntervalMs,
}: CapabilityCheckProps) {
  const { phase, latest, loadingLatest, start, accept, decline } = useCapabilityProbe({
    client,
    orgId,
    canRun,
    pollIntervalMs,
  });
  const [limitedDismissed, setLimitedDismissed] = useState(false);

  const pending = phase.kind === 'quoting' || phase.kind === 'running';
  const isFailed = phase.kind === 'failed';
  const shown: CapabilityRunView | null =
    phase.kind === 'result' || phase.kind === 'failed' ? phase.run : latest;

  return (
    <section className={styles.section} aria-labelledby="capability-heading">
      <h2 id="capability-heading" className={styles.heading}>
        Model capability
      </h2>
      <p className={styles.lede}>
        Check whether your configured model meets the capability floor for AI journey modeling. It
        is never falsely reported as good.
      </p>

      {/* The single announcement channel — visually hidden, announce-only. */}
      <p role="status" aria-live="polite" aria-atomic="true" className={styles.srOnly}>
        {liveMessage(phase)}
      </p>

      {loadingLatest ? (
        <p className={styles.muted} data-testid="capability-loading">
          <Spinner /> Loading…
        </p>
      ) : null}

      {phase.kind === 'error' ? (
        <InlineAlert severity="error">{phase.message}</InlineAlert>
      ) : null}

      {pending ? (
        <p className={styles.muted} data-testid="capability-pending">
          <Spinner /> Checking your model…
        </p>
      ) : isFailed ? (
        <div className={styles.notice} data-testid="capability-failed">
          We couldn’t complete the capability check. This is a temporary problem — please try
          again.
        </div>
      ) : shown ? (
        <Verdict run={shown} />
      ) : !loadingLatest ? (
        <p className={styles.muted} data-testid="capability-empty">
          No capability check has run for this model yet.
        </p>
      ) : null}

      {phase.kind === 'result' && phase.run.status === 'limited' && !limitedDismissed ? (
        <div className={styles.notice} data-testid="limited-warning">
          <span>
            This model is <strong>limited</strong> — it can still be used, but AI journey modeling
            may be lower quality.
          </span>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setLimitedDismissed(true)}
            data-testid="limited-continue"
          >
            Continue
          </Button>
        </div>
      ) : null}

      {canRun ? (
        blockedReason ? (
          <p className={styles.muted} data-testid="capability-blocked">
            {blockedReason}
          </p>
        ) : (
          <div className={styles.actions}>
            <Button type="button" busy={pending} onClick={start} data-testid="capability-run">
              {shown ? 'Check again' : 'Check capability'}
            </Button>
          </div>
        )
      ) : (
        <p className={styles.muted}>Only an organization admin can run a capability check.</p>
      )}

      {phase.kind === 'consent' && phase.quote.estimatedMicroUsd ? (
        <ConsentDialog
          costLabel={formatMicroUsd(phase.quote.estimatedMicroUsd) ?? '~$0'}
          model={shown?.model ?? 'your model'}
          onAccept={() => accept(phase.quote.estimatedMicroUsd as string)}
          onDecline={decline}
        />
      ) : null}
    </section>
  );
}
